import React from 'react';
import { Loader2 } from 'lucide-react';

const sizeClasses = {
  xs: 'px-2 py-1 text-2xs gap-1',
  sm: 'px-3 py-1.5 text-2xs gap-1.5',
  md: 'px-4 py-2 text-xs gap-2',
  lg: 'px-6 py-3 text-sm gap-2',
};

const variantStyles = {
  primary: {
    base: 'font-semibold text-[var(--color-accent-contrast)]',
    bg: 'var(--color-accent-primary)',
    hover: 'hover:brightness-110',
  },
  secondary: {
    base: 'font-medium',
    bg: 'var(--color-bg-tertiary)',
    color: 'var(--color-text-primary)',
    border: 'var(--color-border-primary)',
    hover: 'hover:brightness-110',
  },
  ghost: {
    base: 'font-medium',
    bg: 'transparent',
    color: 'var(--color-text-secondary)',
    hover: 'hover:bg-[color-mix(in_srgb,var(--color-text-primary)_5%,transparent)]',
  },
  danger: {
    base: 'font-semibold text-[var(--color-accent-contrast)]',
    bg: 'var(--color-error)',
    hover: 'hover:brightness-110',
  },
  outline: {
    base: 'font-medium',
    bg: 'transparent',
    color: 'var(--color-text-secondary)',
    border: 'var(--color-border-primary)',
    hover: 'hover:brightness-110',
  },
  accent: {
    base: 'font-semibold text-[var(--color-accent-contrast)]',
    hover: 'hover:brightness-110',
  },
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  accentColor,
  className = '',
  style: styleProp,
  ...props
}) {
  const v = variantStyles[variant] || variantStyles.primary;
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  const computedStyle = {
    backgroundColor: variant === 'accent' && accentColor ? accentColor : v.bg,
    color: v.color,
    border: v.border ? `1px solid ${v.border}` : undefined,
    ...styleProp,
  };

  return (
    <button
      disabled={disabled || loading}
      className={`ui-pressable relative inline-flex items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${v.hover} ${v.base} ${sizeClass} ${className}`}
      style={computedStyle}
      {...props}
    >
      {loading && (
        <Loader2 size={14} className="animate-spin absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
      )}
      <span className={`inline-flex items-center justify-center ${loading ? 'invisible' : ''}`}>
        {children}
      </span>
    </button>
  );
}

export function IconButton({
  children,
  label,
  variant = 'ghost',
  size = 'md',
  className = '',
  style: styleProp,
  ...props
}) {
  const iconSizeClasses = {
    xs: 'p-1',
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const v = variantStyles[variant] || variantStyles.ghost;
  const padClass = iconSizeClasses[size] || iconSizeClasses.md;

  const computedStyle = {
    backgroundColor: v.bg !== 'transparent' ? v.bg : undefined,
    color: v.color,
    ...styleProp,
  };

  return (
    <button
      aria-label={label}
      title={label}
      className={`ui-pressable inline-flex items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 ${v.hover} ${v.base} ${padClass} ${className}`}
      style={computedStyle}
      {...props}
    >
      {children}
    </button>
  );
}
